import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAxiosPrivate from "./useAxiosPrivate";
import useAuth from "./useAuth";
import { EVENT_TYPES, getPlaceholderGroupsForEvent } from "./smsTemplateConfig";

const isRequestCanceled = (err) => err?.code === "ERR_CANCELED" || err?.name === "CanceledError";

function EditTemplate() {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();

    const [name, setName] = useState("");
    const [body, setBody] = useState("");
    const [eventType, setEventType] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [isDefault, setIsDefault] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [loanDefaultRunTime, setLoanDefaultRunTime] = useState("");
    const [birthdayRunTime, setBirthdayRunTime] = useState("");
    const [installmentRunTime, setInstallmentRunTime] = useState("");

    const bodyInputRef = useRef(null);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const fetchTemplate = async () => {
            if (!templateId) {
                setError("Template ID is required to edit a template.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const response = await axiosPrivate.get(`/sms/templates/${templateId}`, {
                    headers: auth?.accessToken ? { Authorization: "Bearer " + auth.accessToken } : undefined,
                    signal: controller.signal
                });

                if (isMounted) {
                    const data = response?.data ?? {};
                    setName(data.name ?? "");
                    setBody(data.body ?? "");
                    setEventType(data.event_type ?? "");
                    setIsActive(Boolean(data.is_active));
                    setIsDefault(Boolean(data.is_default));
                    setLoanDefaultRunTime(data.loan_default_run_time ?? "");
                    setBirthdayRunTime(data.birthday_run_time ?? "");
                    setInstallmentRunTime(data.installment_alert_run_time ?? "");
                }
            } catch (err) {
                if (isMounted && !isRequestCanceled(err)) {
                    setError(err?.response?.data?.message ?? "Failed to load template. Please try again.");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        if (auth?.accessToken) {
            fetchTemplate();
        } else {
            setLoading(false);
            setError("You must be authenticated to edit a template.");
        }

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [axiosPrivate, auth?.accessToken, templateId]);

    const disabled = useMemo(() => {
        if (eventType === "overdueLoan") {
            return submitting || loading || !name.trim() || !body.trim() || !eventType || !loanDefaultRunTime;
        }

        if (eventType === "birthday") {
            return submitting || loading || !name.trim() || !body.trim() || !eventType || !birthdayRunTime;
        }

        if (eventType === "installmentAlert") {
            return submitting || !name.trim() || !body.trim() || !eventType || !installmentRunTime;
        }

        return submitting || loading || !name.trim() || !body.trim() || !eventType;
    }, [birthdayRunTime, body, eventType, loading, loanDefaultRunTime, name, submitting, installmentRunTime]);

    const insertPlaceholder = (groupKey, field) => {
        const textarea = bodyInputRef.current;
        const placeholder = `{{ ${groupKey}.${field} }}`;

        if (!textarea) {
            setBody((prev) => (prev ? `${prev} ${placeholder}` : placeholder));
            return;
        }

        const { selectionStart, selectionEnd } = textarea;

        setBody((prevBody) => {
            const start = typeof selectionStart === "number" ? selectionStart : prevBody.length;
            const end = typeof selectionEnd === "number" ? selectionEnd : start;
            const updated = prevBody.slice(0, start) + placeholder + prevBody.slice(end);

            const restoreCursor = () => {
                const caret = start + placeholder.length;
                textarea.focus();
                textarea.setSelectionRange(caret, caret);
            };

            if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
                window.requestAnimationFrame(restoreCursor);
            } else {
                restoreCursor();
            }

            return updated;
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (disabled) {
            return;
        }

        setSubmitting(true);
        setError(null);

        const payload = {
            name: name.trim(),
            body: body.trim(),
            event_type: eventType,
            is_active: isActive,
            is_default: isDefault,
            loan_default_run_time: eventType === "overdueLoan" ? loanDefaultRunTime : null,
            birthday_run_time: eventType === "birthday" ? birthdayRunTime : null,
            installment_alert_run_time: eventType === "installmentAlert" ? installmentRunTime : null
        };

        try {
            await axiosPrivate.put(`/sms/templates/${templateId}`, payload, {
                headers: auth?.accessToken ? { Authorization: "Bearer " + auth.accessToken } : undefined
            });
            navigate("/sms");
        } catch (err) {
            const message = err?.response?.data?.message ?? "Failed to update template. Please try again.";
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const placeholderGroups = useMemo(() => getPlaceholderGroupsForEvent(eventType), [eventType]);

    if (loading) {
        return (
            <div className="max-w-3xl mx-auto rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <p className="text-sm text-gray-500">Loading template…</p>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-gray-900">Edit SMS Template</h1>
                <p className="text-sm text-gray-500">Update the SMS template details and placeholders.</p>
            </div>

            {error && (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                    <label htmlFor="template-name" className="mb-1 block text-sm font-medium text-gray-700">
                        Template Name
                    </label>
                    <input
                        id="template-name"
                        type="text"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        required
                        placeholder="E.g. Welcome Message"
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                </div>

                <div>
                    <label htmlFor="event-type" className="mb-1 block text-sm font-medium text-gray-700">
                        Event Type
                    </label>
                    <select
                        id="event-type"
                        value={eventType}
                        onChange={(event) => setEventType(event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                        <option value="" disabled>
                            Select an event type
                        </option>
                        {EVENT_TYPES.map(({ value, label }) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </select>
                </div>

                {eventType === "overdueLoan" && (
                    <div>
                        <label htmlFor="loan-default-run-time" className="mb-1 block text-sm font-medium text-gray-700">
                            Loan Default Run Time
                        </label>
                        <input
                            id="loan-default-run-time"
                            type="time"
                            value={loanDefaultRunTime}
                            onChange={(event) => setLoanDefaultRunTime(event.target.value)}
                            required
                            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">Set the time to send overdue loan reminders.</p>
                    </div>
                )}

                {eventType === "birthday" && (
                    <div>
                        <label htmlFor="birthday-run-time" className="mb-1 block text-sm font-medium text-gray-700">
                            Birthday Message Time
                        </label>
                        <input
                            id="birthday-run-time"
                            type="time"
                            value={birthdayRunTime}
                            onChange={(event) => setBirthdayRunTime(event.target.value)}
                            required
                            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">Choose when to send birthday greetings.</p>
                    </div>
                )}

                {eventType === "installmentAlert" && (
                    <div>
                        <label htmlFor="installment-alert-run-time" className="mb-1 block text-sm font-medium text-gray-700">
                            Installment Alert Time
                        </label>
                        <input
                            id="birthday-run-time"
                            type="time"
                            value={installmentRunTime}
                            onChange={(event) => setInstallmentRunTime(event.target.value)}
                            required
                            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">Choose when to send installment alerts.</p>
                    </div>
                )}

                <div>
                    <label htmlFor="template-body" className="mb-1 block text-sm font-medium text-gray-700">
                        Message Body
                    </label>
                    <textarea
                        id="template-body"
                        ref={bodyInputRef}
                        value={body}
                        onChange={(event) => setBody(event.target.value)}
                        required
                        rows={6}
                        placeholder="Write the SMS content here..."
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase text-gray-500">Insert placeholders</p>
                        <p className="mt-1 text-xs text-gray-500">
                            Click a placeholder to add it to the message body at the current cursor position.
                        </p>
                        <div className="mt-3 space-y-4">
                            {placeholderGroups.map(({ key, label, fields }) => (
                                <div key={key}>
                                    <h3 className="text-sm font-medium text-gray-700">{label} placeholders</h3>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {fields.map((field) => (
                                            <button
                                                key={field}
                                                type="button"
                                                onClick={() => insertPlaceholder(key, field)}
                                                className="rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs font-medium text-indigo-600 transition hover:border-indigo-300 hover:bg-indigo-50"
                                            >
                                                {`{{ ${key}.${field} }}`}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap gap-6">
                    <label className="flex items-center space-x-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={isActive}
                            onChange={(event) => setIsActive(event.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span>Active</span>
                    </label>

                    <label className="flex items-center space-x-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={isDefault}
                            onChange={(event) => setIsDefault(event.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span>Set as default</span>
                    </label>
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        disabled={submitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={disabled}
                    >
                        {submitting ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default EditTemplate;
